<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  IonApp,
  IonRouterOutlet,
  IonMenu,
  IonHeader,
  IonContent,
  IonIcon,
  menuController,
  toastController,
} from '@ionic/vue';
import {
  gridOutline,
  receiptOutline,
  cubeOutline,
  settingsOutline,
  logOutOutline,
  personOutline,
  peopleOutline,
  calendarOutline,
  locationOutline,
  desktopOutline,
  notificationsOutline,
  barChartOutline,
  carOutline,
  returnUpBackOutline,
  alertCircleOutline,
} from 'ionicons/icons';
import rmLogo from '@/assets/img/rm-logo-white.png';
import { getLocations, cacheLocations, chooseLocation, getLocationScopedToken, type ApiLocation, type ApiTerminal } from '@/api/locations';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { registerDeviceToken } from '@/api/notifications';
import { BASE_URL } from '@/api/http';
import { Device } from '@capacitor/device';
import { useSelectedLocation } from '@/composables/useSelectedLocation';
import { useCartSession } from '@/composables/useCartSession';
import { useAuthState } from '@/composables/useAuthState';
import { refreshCurrentUser } from '@/composables/useCurrentUser';
import { useNotificationStore } from '@/composables/useNotificationStore';
import { resolveNotifRoute } from '@/utils/notifRoutes';
import { resetSession } from '@/composables/useSessionReset';
import { isBiometricEnabled, syncBiometricRefreshToken } from '@/composables/useBiometricAuth';
import type { PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';

// ── Location / terminal state ────────────────────────────────────────────────

const locations = ref<ApiLocation[]>([]);
const selectedLocationId = ref<number>(0);
const selectedTerminalId = ref<number | null>(null);
const { locationId: sharedLocationId } = useSelectedLocation();
const { itemCount: cartItemCount } = useCartSession();

const currentLocation = computed<ApiLocation | undefined>(() =>
  locations.value.find(l => l.id === selectedLocationId.value),
);

const activeLocations = computed<ApiLocation[]>(() =>
  locations.value.filter(l => l.status === 1),
);

const currentTerminals = computed<ApiTerminal[]>(() =>
  currentLocation.value?.stores_terminals ?? [],
);

function loadFromStorage(): void {
  try {
    const raw = localStorage.getItem('auth_user');
    if (!raw) return;
    const user = JSON.parse(raw) as Record<string, unknown>;
    if (typeof user['location_id'] === 'number') {
      selectedLocationId.value = user['location_id'];
      sharedLocationId.value   = user['location_id'];
    }
    if (typeof user['terminal_id'] === 'number' || user['terminal_id'] === null) {
      selectedTerminalId.value = user['terminal_id'] as number | null;
    }
  } catch {
    // best-effort
  }
}

async function loadLocations(): Promise<void> {
  try {
    const cached = localStorage.getItem('locations');
    if (cached) locations.value = JSON.parse(cached) as ApiLocation[];
    const fresh = await getLocations();
    locations.value = fresh;
    if (localStorage.getItem('auth_token')) {
      cacheLocations(fresh);
    }
  } catch {
    // network failure — cached data already in place if available
  }
}

function persistChoice(): void {
  try {
    const raw = localStorage.getItem('auth_user');
    const user = (raw ? JSON.parse(raw) : {}) as Record<string, unknown>;
    user['location_id'] = selectedLocationId.value;
    user['terminal_id'] = selectedTerminalId.value;
    localStorage.setItem('auth_user', JSON.stringify(user));
  } catch {
    // best-effort
  }
}

// Matches CLI's chooseStore() + _updateToken() (header-nav.component.ts): tell the
// backend which location/terminal is now active, then fetch a fresh, location-scoped
// token — confirmed via a live captured response that `location` is baked straight
// into this token's own JWT payload, which is exactly why the old token kept
// authorizing requests against the old location regardless of any location_id query
// param. CLI reloads its whole app on the new token; this just swaps auth_token and
// refreshes the cached user profile (access_details/permissions can differ per
// location) in place.
async function notifyLocationChoice(): Promise<void> {
  try {
    await chooseLocation(selectedLocationId.value, selectedTerminalId.value);
  } catch (e: unknown) {
    console.error('[location] chooseLocation failed:', e);
    return;
  }
  try {
    const result = await getLocationScopedToken(selectedLocationId.value);
    localStorage.setItem('auth_token', result.token);
    localStorage.setItem('refresh_token', result.refresh_token);
    if (isBiometricEnabled()) {
      syncBiometricRefreshToken(result.refresh_token).catch(() => { /* non-fatal */ });
    }
    const raw = localStorage.getItem('auth_user');
    const user = (raw ? JSON.parse(raw) : {}) as Record<string, unknown>;
    const { token: _droppedToken, refresh_token: _droppedRefresh, ...profile } = result;
    localStorage.setItem('auth_user', JSON.stringify({
      ...user,
      ...profile,
      location_id: Number(result.location_id),
    }));
  } catch (e: unknown) {
    console.error('[location] post-switch token fetch failed:', e);
  }
}

// Matches CLI's selectStore() (header-nav.component.ts): block the switch entirely
// while a cart is in progress — items already added, live prices, and the server
// cart token are all scoped to the location they were added under, so silently
// swapping it out from under an active order risks pricing/availability mismatches
// no reactive refetch can fix. CLI shows the same "finish your order first" alert.
async function showLocationAlert() {
  const t = await toastController.create({
    message:  'Finish the current order before changing locations.',
    duration: 3000,
    position: 'top',
    cssClass: 'app-toast toast-warning',
    icon:     alertCircleOutline,
  });
  await t.present();
}

async function onLocationChange(e: Event): Promise<void> {
  const select = e.target as HTMLSelectElement;
  if (cartItemCount.value > 0) {
    select.value = String(selectedLocationId.value);
    void showLocationAlert();
    return;
  }
  const id = Number(select.value);
  // selectedLocationId updates the dropdown's own display immediately, but
  // sharedLocationId — the signal every page's reload is wired to — only flips
  // once choose + refresh have actually finished. Every list page fetches the
  // instant sharedLocationId changes, so flipping it first meant that fetch raced
  // ahead of locations/choose and the token refresh, firing with the right
  // location_id in its query string but before the backend/token were told about
  // the switch at all.
  selectedLocationId.value = id;
  const firstTerminal = currentLocation.value?.stores_terminals?.[0] ?? null;
  selectedTerminalId.value = firstTerminal?.id ?? null;
  persistChoice();
  await notifyLocationChoice();
  sharedLocationId.value = id;
}

function onTerminalChange(e: Event): void {
  const val = (e.target as HTMLSelectElement).value;
  selectedTerminalId.value = val ? Number(val) : null;
  persistChoice();
  void notifyLocationChoice();
}

// ── Push notifications ───────────────────────────────────────────────────────

const push = usePushNotifications();
const notifStore = useNotificationStore();

type NotifData = Record<string, string>;

async function showPushToast(title: string, body: string) {
  const t = await toastController.create({
    header:   title || 'New Notification',
    message:  body  || '',
    duration: 4000,
    position: 'top',
    cssClass: 'app-toast toast-success',
    icon:     notificationsOutline,
  });
  await t.present();
}

const { justLoggedIn } = useAuthState();

function initPush() {
  push.listen({
    onToken: (token) => {
      console.log('[FCM] active token:', token);
      const force = justLoggedIn.value;
      justLoggedIn.value = false;
      registerDeviceToken(token, force).catch((e: unknown) => {
        console.error('[FCM] registerDeviceToken failed:', e);
      });
    },
    onReceived: (n: PushNotificationSchema) => {
      // Raw push payload exactly as the server sent it — title/body here are pre-built
      // server-side (we never construct this text client-side, same as CLI's own notification
      // bell just displaying its `note` field verbatim), so this is the place to see whether a
      // missing order number / $0.00 amount originates from the server's payload itself vs.
      // something being lost afterward on the client.
      console.log('[NOTIF] raw push received==========>:', n);
      console.log('[NOTIF]   title==========>:', n.title);
      console.log('[NOTIF]   body==========>:', n.body);
      console.log('[NOTIF]   data==========>:', n.data);
      notifStore.addNotification({
        id:    n.id ?? String(Date.now()),
        title: n.title ?? '',
        body:  n.body  ?? '',
        data:  (n.data ?? {}) as NotifData,
      });
      showPushToast(n.title ?? 'New Notification', n.body ?? '');
    },
    onAction: (a: ActionPerformed) => {
      console.log('[NOTIF] raw push tapped==========>:', a.notification);
      console.log('[NOTIF]   title==========>:', a.notification.title);
      console.log('[NOTIF]   body==========>:', a.notification.body);
      console.log('[NOTIF]   data==========>:', a.notification.data);
      const n = a.notification;
      const data = (n.data ?? {}) as NotifData;
      notifStore.addNotification({
        id:    n.id ?? String(Date.now()),
        title: n.title ?? '',
        body:  n.body  ?? '',
        data,
      });
      const route = resolveNotifRoute(data);
      if (route) router.push(route);
    },
  });

  // If already logged in (app reopen), re-register for push
  if (localStorage.getItem('auth_token')) {
    push.requestPermission().catch(() => { /* non-fatal */ });
  }
}

onMounted(() => {
  console.log('[API] base url:', BASE_URL);
  Device.getId().then(r => console.log('[DEVICE] id:', r.identifier)).catch(() => {});
  notifStore.init().catch(() => { /* non-fatal */ });
  loadFromStorage();
  loadLocations();
  initPush();
});

// ── Navigation ───────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  icon: string;
  route: string;
}

const sideNavItems: NavItem[] = [
  { label: 'Dashboard',  icon: gridOutline,     route: 'home' },
  { label: 'Orders',     icon: receiptOutline,  route: 'orders' },
  { label: 'Pickups',    icon: carOutline,      route: 'pickups' },
  { label: 'Returns',    icon: returnUpBackOutline, route: 'returns' },
  { label: 'Inventory',  icon: cubeOutline,     route: 'inventory-hub' },
  { label: 'Customers',  icon: peopleOutline,   route: 'customers' },
  { label: 'Calendar',   icon: calendarOutline, route: 'calendar' },
  { label: 'Reports',    icon: barChartOutline, route: 'reports' },
  { label: 'Settings',   icon: settingsOutline, route: 'settings' },
  { label: 'Profile',    icon: personOutline,   route: 'profile' },
];

const router = useRouter();
const route = useRoute();
const currentRouteName = computed(() => route.name);

// ion-menu is a persistent sibling of the router-outlet, so with no gating it
// stays swipe-openable from literally any screen, including a logged-out Login
// page — exposing the full authenticated nav before signing in.
const PUBLIC_ROUTE_NAMES = new Set(['splash', 'login']);
const isMenuDisabled = computed(
  () => typeof currentRouteName.value === 'string' && PUBLIC_ROUTE_NAMES.has(currentRouteName.value),
);

// Re-sync location/store state and user profile after a fresh login
watch(
  () => route.name,
  (newName, oldName) => {
    if (newName === 'login') {
      selectedLocationId.value = 0;
      selectedTerminalId.value = null;
    }
    if (oldName === 'login' && newName !== 'login') {
      loadFromStorage();
      loadLocations();
      void refreshCurrentUser();
    }
  },
);

function go(routeName: string): void {
  if (router.hasRoute(routeName)) {
    router.push({ name: routeName });
  }
}

async function goFromMenu(routeName: string): Promise<void> {
  await menuController.close('app-menu');
  go(routeName);
}

async function signOut(): Promise<void> {
  await resetSession();
  // Preserve biometric_enabled so the user can log back in with fingerprint/Face ID.
  // The refresh token stored in Capacitor Preferences (the biometric credential) is
  // intentionally left intact — it is the key that powers biometric re-login.
  const biometricEnabled = localStorage.getItem('biometric_enabled');
  localStorage.clear();
  if (biometricEnabled) localStorage.setItem('biometric_enabled', biometricEnabled);
  router.replace({ name: 'login' });
}

async function onMenuSignOut(): Promise<void> {
  await menuController.close('app-menu');
  await signOut();
}
</script>

<template>
  <ion-app>

    <!-- Side menu — direct child of ion-app so Ionic manages it correctly -->
    <ion-menu
      menu-id="app-menu"
      content-id="main-content"
      side="start"
      type="overlay"
      :disabled="isMenuDisabled"
    >

      <!-- Red header: logo + ADMIN label -->
      <ion-header class="ion-no-border">
        <div class="sidebar-header">
          <img :src="rmLogo" alt="RentMy" class="sidebar-logo" />
          <span class="sidebar-admin-label">ADMIN</span>
        </div>
      </ion-header>

      <!-- Scrollable body: location selects + nav + sign out -->
      <ion-content class="sidebar-content">

        <!-- Location & Terminal dropdowns -->
        <div class="sidebar-selects">
          <div class="sidebar-select-group">
            <label class="sidebar-select-label">
              <ion-icon :icon="locationOutline" class="sidebar-select-label__icon" />
              <span>Location</span>
            </label>
            <div class="sidebar-select-wrap">
              <select
                class="sidebar-select"
                :value="selectedLocationId"
                @change="onLocationChange"
              >
                <option value="0" disabled>Select location</option>
                <option v-for="loc in activeLocations" :key="loc.id" :value="loc.id">
                  {{ loc.name }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="currentTerminals.length > 0" class="sidebar-select-group">
            <label class="sidebar-select-label">
              <ion-icon :icon="desktopOutline" class="sidebar-select-label__icon" />
              <span>Terminal</span>
            </label>
            <div class="sidebar-select-wrap">
              <select
                class="sidebar-select"
                :value="selectedTerminalId ?? ''"
                @change="onTerminalChange"
              >
                <option value="0" disabled>Select Terminal</option>
                <option v-for="ter in currentTerminals" :key="ter.id" :value="ter.id">
                  {{ ter.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Nav links -->
        <nav class="sidebar-nav">
          <button
            v-for="item in sideNavItems"
            :key="item.route"
            type="button"
            class="sidebar-nav-item"
            :class="{ 'sidebar-nav-item--active': item.route === currentRouteName }"
            @click="goFromMenu(item.route)"
          >
            <ion-icon :icon="item.icon" class="sidebar-nav-item__icon" />
            <span>{{ item.label }}</span>
          </button>
        </nav>

        <div class="sidebar-divider" role="separator" />

        <!-- Sign Out -->
        <button type="button" class="sidebar-nav-item sidebar-signout" @click="onMenuSignOut">
          <ion-icon :icon="logOutOutline" class="sidebar-nav-item__icon sidebar-signout__icon" />
          <span>Sign Out</span>
        </button>

      </ion-content>
    </ion-menu>

    <ion-router-outlet id="main-content" />

  </ion-app>
</template>
